import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { API_BASE } from '../config/api';
import { authHeaders, parseApiError } from '../utils/apiClient';
import './Dashboard.css';

const SESSION_TYPES = {
  MINI_INSIGHT: { name: 'Mini Insight Session', duration: 30 },
  COMPREHENSIVE_READING: { name: 'Comprehensive Reading', duration: 60 },
  FULL_BREAKTHROUGH: { name: 'Full Breakthrough Reading', duration: 90 }
};

const Dashboard = () => {
  const { user, logout, getToken } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBookings = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/slots/my-bookings?email=${user.email}`, {
        headers: authHeaders(token)
      });
      
      if (response.ok) {
        const data = await response.json();
        setBookings(data);
      }
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    } finally {
      setLoading(false);
    }
  }, [user.email, getToken]);

  useEffect(() => {
    if (user?.email) {
      fetchBookings();
    }
  }, [user?.email, fetchBookings]);

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    return `${hour12}:${minutes} ${ampm}`;
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) {
      return;
    }

    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/slots/${bookingId}?email=${user.email}`, {
        method: 'DELETE',
        headers: authHeaders(token)
      });

      if (response.ok) {
        alert('Booking cancelled successfully');
        fetchBookings();
      } else {
        alert(`Failed to cancel booking: ${await parseApiError(response)}`);
      }
    } catch (error) {
      console.error('Error cancelling booking:', error);
      alert('Failed to cancel booking');
    }
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <Navigation />
        <div className="dashboard-container">
          <p>Loading...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <Navigation />
      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1>Welcome, {user?.firstName || 'User'}!</h1>
          <button onClick={logout} className="logout-btn">Logout</button>
        </div>
        
        <div className="bookings-section">
          <h2>Your Bookings</h2>
          {bookings.length === 0 ? (
            <p>No bookings found. <Link to="/booking">Book your first session!</Link></p>
          ) : (
            <div className="bookings-list">
              {bookings.map((booking) => (
                <div key={booking.id} className="booking-card">
                  <div className="booking-info">
                    <h3>{SESSION_TYPES[booking.sessionType]?.name || booking.service}</h3>
                    <p><strong>Date:</strong> {booking.bookingDate}</p>
                    <p><strong>Time:</strong> {formatTime(booking.startTime)} - {formatTime(booking.endTime)}</p>
                    <p><strong>Duration:</strong> {SESSION_TYPES[booking.sessionType]?.duration || 60} minutes</p>
                    <p><strong>Email:</strong> {booking.email}</p>
                  </div>
                  <button 
                    onClick={() => handleCancelBooking(booking.id)}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Dashboard;